import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../contexts/OrganizationContext';

function ThankYouPage({ confirmationNumber }) {
  const { t } = useTranslation();
  const { organization } = useOrganization();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-2xl w-full">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <svg 
              className="w-16 h-16 text-green-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('thankYou.title')}
          </h1>
          
          <p className="text-xl text-gray-700 mb-2">
            {t('thankYou.subtitle')}
          </p>
          
          <p className="text-lg text-gray-600">
            {t('thankYou.received')}
          </p>
        </div>

        {/* Information Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            {t('thankYou.nextSteps.title')}
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t('thankYou.nextSteps.step1.title')}</h3>
                <p className="text-gray-600">
                  {t('thankYou.nextSteps.step1.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <span className="text-blue-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t('thankYou.nextSteps.step2.title')}</h3>
                <p className="text-gray-600">
                  {t('thankYou.nextSteps.step2.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <span className="text-blue-600 font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t('thankYou.nextSteps.step3.title')}</h3>
                <p className="text-gray-600">
                  {t('thankYou.nextSteps.step3.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <span className="text-blue-600 font-bold">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t('thankYou.nextSteps.step4.title')}</h3>
                <p className="text-gray-600">
                  {t('thankYou.nextSteps.step4.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-blue-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {t('thankYou.contact.title')}
          </h3>
          <p className="text-gray-700 mb-4">
            {t('thankYou.contact.subtitle')}
          </p>
          <div className="space-y-2">
            <p className="text-gray-800">
              <span className="font-semibold">{t('thankYou.contact.email')}</span> {organization?.admin_email || organization?.email || 'Contact us'}
            </p>
            <p className="text-gray-800">
              <span className="font-semibold">{t('thankYou.contact.phone')}</span> {organization?.company_phone || organization?.phone || 'Contact us'}
            </p>
            <p className="text-gray-800">
              <span className="font-semibold">{t('thankYou.contact.hours')}</span> {organization?.business_hours || 'Monday - Sunday, 9:00 AM - 9:00 PM EST'}
            </p>
          </div>
        </div>

        {/* Confirmation Number */}
        {confirmationNumber && (
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500 mb-6">
            <p className="text-sm text-gray-600 mb-1">{t('thankYou.confirmation.title')}</p>
            <p className="text-2xl font-mono font-bold text-gray-900">
              {confirmationNumber}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {t('thankYou.confirmation.note')}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.print()}
            className="bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            {t('thankYou.actions.print')}
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-gray-200 text-gray-700 py-3 px-8 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            {t('thankYou.actions.home')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ThankYouPage;
